<template>
    <div class="tw:inline-flex" :class="{'tw:w-full': expanded}">
        <FileUpload
            ref="primeUpload"
            custom-upload
            :show-upload-button="false"
            :show-cancel-button="false"
            :multiple="multiple"
            :accept="accept"
            auto
            :disabled="disabled"
            :pt="{ root: { class: 'tw:hidden' } }"
            @select="handleFileSelect"
        />
        <div
            v-if="!dragDrop"
            v-bind="wrapperAttrs"
            :class="{'tw:cursor-pointer': !disabled, 'tw:opacity-50 tw:cursor-not-allowed': disabled, 'tw:w-full': expanded}"
            @click="openFileDialog"
        >
            <slot />
        </div>
        <div
            v-else
            class="upload-draggable"
            :class="dragDropClasses"
            @click="openFileDialog"
            @dragover.prevent="updateDragDropFocus(true)"
            @dragleave.prevent="updateDragDropFocus(false)"
            @dragenter.prevent="updateDragDropFocus(true)"
            @drop.prevent="onFileChange"
        >
            <slot />
        </div>
    </div>
</template>

<script>
import { computed, ref, useAttrs } from 'vue';

import { FILE_UPLOAD_CONTEXT, FILE_UPLOAD_REJECTION_REASON } from '@/constants/fileUploadRejection.js';

/**
 * EUpload - Abstraction wrapper around a custom PrimeVue file uploader to replicate Buefy's b-upload
 *
 * This component mimics the behavior of b-upload by using a hidden PrimeVue FileUpload component
 * and a visible slot that acts as a trigger. This approach ensures a consistent look and feel
 * between the Buefy and PrimeVue implementations.
 */
export default {
    name: 'EUpload',
    inheritAttrs: false,
    props: {
        modelValue: {
            type: [File, Array],
            default: null,
        },
        rounded: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        accept: {
            type: String,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dragDrop: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: 'is-primary',
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        native: {
            type: Boolean,
            default: false,
        },
        uploadContext: {
            type: String,
            default: FILE_UPLOAD_CONTEXT.UNSPECIFIED,
        },
        /**
         * Set false when the parent re-validates dropped files itself (e.g. a wrapper drop handler),
         * so a drop rejected by `accept` here is not logged twice or logged for a file the parent accepts.
         */
        logDropRejections: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:modelValue', 'invalid'],
    setup(props, { emit }) {
        const attrs = useAttrs();
        const primeUpload = ref(null);
        const dragDropFocus = ref(false);

        const checkType = file => {
            if (!props.accept) {
                return true;
            }
            const types = props.accept.split(',');
            if (types.length === 0) {
                return true;
            }
            let valid = false;
            for (let i = 0; i < types.length && !valid; i++) {
                const type = types[i].trim();
                if (type) {
                    if (type.substring(0, 1) === '.') {
                        // check extension
                        const extension = file.name.toLowerCase().slice(-type.length);
                        if (extension === type.toLowerCase()) {
                            valid = true;
                        }
                    } else if (file.type.match(type)) {
                        // check mime type
                        valid = true;
                    }
                }
            }
            if (!valid) {
                emit('invalid');
            }
            return valid;
        };

        /**
         * The helper is imported on demand because vitest.setup.js registers EUpload globally: a static
         * import would load the logging service and the shared axios client before any spec could mock them.
         */
        const reportUnsupportedFiles = rejectedFiles => {
            if (rejectedFiles.length === 0) {
                return;
            }

            import('@/utils/reportFileUploadRejection.js')
                .then(({ reportFileUploadRejections }) => {
                    reportFileUploadRejections(
                        props.uploadContext,
                        rejectedFiles.map(file => ({
                            file,
                            reason: FILE_UPLOAD_REJECTION_REASON.UNSUPPORTED_FILE_TYPE,
                        }))
                    );
                })
                .catch(() => {});
        };

        const handleFileSelect = event => {
            const { files } = event;
            if (props.accept && Array.isArray(files)) {
                // PrimeVue drops files that do not match `accept` from `files` without telling us.
                const { originalEvent } = event;
                const selectedFiles = Array.from(
                    originalEvent?.dataTransfer?.files ?? originalEvent?.target?.files ?? []
                );
                reportUnsupportedFiles(selectedFiles.filter(file => !files.includes(file)));
            }
            if (props.multiple) {
                emit('update:modelValue', files);
            } else {
                emit('update:modelValue', files[0]);
            }

            if (primeUpload.value) {
                // Clear the selection so the same file can be selected again
                primeUpload.value.clear();
            }
        };

        const onFileChange = event => {
            if (props.disabled) {
                return;
            }
            if (props.dragDrop) {
                dragDropFocus.value = false;
            }

            const value = event.target.files || event.dataTransfer.files || [];

            if (value.length === 0) {
                if (!props.modelValue) return;
                if (props.native) emit('update:modelValue', null);
            } else if (!props.multiple) {
                // only one element in case drag drop mode and isn't multiple
                if (props.dragDrop && value.length !== 1) return;

                const file = value[0];
                if (checkType(file)) {
                    emit('update:modelValue', file);
                } else if (props.logDropRejections) {
                    reportUnsupportedFiles([file]);
                }
            } else {
                // always new values if native or undefined local
                const newFiles = [];
                const unsupportedFiles = [];
                for (let i = 0; i < value.length; i++) {
                    const file = value[i];
                    if (checkType(file)) {
                        newFiles.push(file);
                    } else {
                        unsupportedFiles.push(file);
                    }
                }
                if (props.logDropRejections) {
                    reportUnsupportedFiles(unsupportedFiles);
                }
                if (newFiles.length > 0) {
                    let currentFiles = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
                    if (props.native || !props.modelValue) {
                        currentFiles = [];
                    }
                    currentFiles.push(...newFiles);
                    emit('update:modelValue', currentFiles);
                }
            }
        };

        const openFileDialog = () => {
            if (props.disabled) return;
            if (primeUpload.value) {
                primeUpload.value.choose();
            }
        };

        const updateDragDropFocus = focus => {
            if (!props.disabled) {
                dragDropFocus.value = focus;
            }
        };

        const wrapperAttrs = computed(() => {
            const newAttrs = { ...attrs };
            const classes = [attrs.class];

            if (props.rounded) {
                classes.push('tw:rounded-full');
            }

            if (props.expanded) {
                classes.push('tw:w-full');
            }

            newAttrs.class = classes;
            return newAttrs;
        });

        const dragDropClasses = computed(() => {
            return [
                props.type,
                {
                    'is-disabled': props.disabled,
                    'is-hovered': dragDropFocus.value,
                    'tw:w-full': props.expanded,
                    'tw:border-dashed tw:border-2 tw:border-gray-300 tw:rounded-lg tw:p-4 tw:text-center tw:cursor-pointer tw:transition-colors':
                        props.dragDrop,
                    'tw:bg-gray-100 tw:border-primary-500': props.dragDrop && dragDropFocus.value,
                    'tw:opacity-50 tw:cursor-not-allowed': props.disabled,
                },
            ];
        });

        return {
            handleFileSelect,
            openFileDialog,
            primeUpload,
            wrapperAttrs,
            onFileChange,
            updateDragDropFocus,
            dragDropClasses,
        };
    },
};
</script>
